import React, { useState, useEffect } from 'react';
import { Store, Share2, CheckCircle } from 'lucide-react';
import { usePharmacySettings, useUpdatePharmacySettings } from '../../hooks/settings/usePharmacySettings';
import styles from './PharmacySettings.module.css';

// GET /pharmacy/settings 응답 → 화면 폼
const toForm = (data) => ({
  pharmacyInfo: {
    name: data?.pharmacy_info?.name || '',
    businessNumber: data?.pharmacy_info?.business_number || '',
    address: data?.pharmacy_info?.address || '',
    phone: data?.pharmacy_info?.phone || '',
  },
  socialLinks: {
    instagram: data?.social_links?.instagram || '',
    threads: data?.social_links?.threads || '',
    naverBlog: data?.social_links?.naver_blog || '',
  },
});

/**
 * 약국 설정 화면 — 서버(/pharmacy/settings) 저장
 */
const PharmacySettings = () => {
  const { data, isError } = usePharmacySettings();
  const updateSettings = useUpdatePharmacySettings();
  const [settings, setSettings] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // 조회 실패 시에도 같은 폼 구조로 빈 값 표시 (별도 에러 화면 없음)
  useEffect(() => {
    if (data || isError) setSettings(toForm(data));
  }, [data, isError]);

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    const newSettings = { ...settings };

    let current = newSettings;
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;

    setSettings(newSettings);
    setIsDirty(true);
    setIsSaved(false);
  };

  const handleSave = async () => {
    if (!isDirty || updateSettings.isPending) return;
    try {
      // 바뀐 기본정보만 전송 (서버는 빈 약국명·주소를 거부, business_number 는 수정 불가라 보내지 않음)
      const saved = toForm(data).pharmacyInfo;
      const pharmacyInfo = {};
      ['name', 'address', 'phone'].forEach(key => {
        if (settings.pharmacyInfo[key] !== saved[key]) pharmacyInfo[key] = settings.pharmacyInfo[key];
      });
      await updateSettings.mutateAsync({
        pharmacy_info: pharmacyInfo,
        social_links: {
          instagram: settings.socialLinks.instagram,
          threads: settings.socialLinks.threads,
          naver_blog: settings.socialLinks.naverBlog,
        },
      });
      setIsSaved(true);
      setIsDirty(false);
      setTimeout(() => setIsSaved(false), 3000);
    } catch (error) {
      alert(error.message);
    }
  };

  if (!settings) {
    return <div className={styles.loading}>로딩 중...</div>;
  }

  const saveButtonClass = isSaved
    ? `${styles.saveButton} ${styles.saved}`
    : isDirty
      ? `${styles.saveButton} ${styles.active}`
      : styles.saveButton;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.cardRow}>
        {/* 약국 정보 카드 */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIconBadge}>
              <Store size={18} />
            </div>
            <h2 className={styles.cardTitle}>약국 정보</h2>
          </div>

          <div className={styles.cardBody}>
            <div className={styles.formGroup}>
              <label>약국명</label>
              <input
                type="text"
                placeholder="약국명을 입력하세요"
                value={settings.pharmacyInfo.name}
                onChange={e =>
                  handleInputChange('pharmacyInfo.name', e.target.value)
                }
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>사업자번호</label>
              <input
                type="text"
                placeholder="사업자번호를 입력하세요"
                value={settings.pharmacyInfo.businessNumber}
                readOnly
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>주소</label>
              <input
                type="text"
                placeholder="주소를 입력하세요"
                value={settings.pharmacyInfo.address}
                onChange={e =>
                  handleInputChange('pharmacyInfo.address', e.target.value)
                }
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>전화번호</label>
              <input
                type="tel"
                placeholder="전화번호를 입력하세요"
                value={settings.pharmacyInfo.phone}
                onChange={e =>
                  handleInputChange('pharmacyInfo.phone', e.target.value)
                }
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.cardFooter}>
            <button className={saveButtonClass} onClick={handleSave}>
              {isSaved ? (
                <>
                  <CheckCircle size={18} />
                  저장 완료
                </>
              ) : (
                '저장'
              )}
            </button>
          </div>
        </div>

        {/* SNS 링크 카드 */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIconBadge}>
              <Share2 size={18} />
            </div>
            <h2 className={styles.cardTitle}>SNS 링크</h2>
          </div>

          <div className={styles.cardBody}>
            <div className={styles.formGroup}>
              <label>인스타그램</label>
              <input
                type="text"
                placeholder="@username 또는 전체 URL"
                value={settings.socialLinks.instagram}
                onChange={e =>
                  handleInputChange('socialLinks.instagram', e.target.value)
                }
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>스레드</label>
              <input
                type="text"
                placeholder="@username 또는 전체 URL"
                value={settings.socialLinks.threads}
                onChange={e =>
                  handleInputChange('socialLinks.threads', e.target.value)
                }
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>네이버 블로그</label>
              <input
                type="text"
                placeholder="블로그 URL"
                value={settings.socialLinks.naverBlog}
                onChange={e =>
                  handleInputChange('socialLinks.naverBlog', e.target.value)
                }
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.cardFooter}>
            <button className={saveButtonClass} onClick={handleSave}>
              {isSaved ? (
                <>
                  <CheckCircle size={18} />
                  저장 완료
                </>
              ) : (
                '저장'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacySettings;
